import './paypal-button';
import store from '../../../store/store';
import { CartPaymentMethod, CartPaymentMethodTemplate } from '../cart-payment-method';
import { PaymentDetails, RmsBookstoreAddress } from '../../checkout/shop-checkout.interface';
import { StoreState } from '../../../store/store.interface';
import { booleanConverter } from '../../../common/converters';
import { connect } from 'pwa-helpers';
import { customElement, property } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import { isLoadingPaypalDependencies } from './utils/paypal.util';
import { loading } from '../../../common/constants/api-constants';
import {
  updateBillingAddress,
  updatePaymentDetails,
  updatePaypalBillingAddress,
  updateProcessingPaypalInfo,
  updateShippingAddress
} from '../../checkout/shop-checkout';

/**
 * Paypal payment method that loads Paypal button in the shopping cart page.
 */
@customElement(`mhecomm-cart-paypal-payment-method`)
export class PaypalCartPaymentMethod extends connect(store)(CartPaymentMethod) implements CartPaymentMethodTemplate {

  @property({converter: booleanConverter})
  private showPaylater = true;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mhecomm-paypal-checkout-info-collected', this.onCollectPaypalInfo);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mhecomm-paypal-checkout-info-collected', this.onCollectPaypalInfo);
  }

  /**
   * Verifies if the dependencies for Paypal are being loaded.
   */
  override isLoadingDependencies(): boolean {
    return isLoadingPaypalDependencies();
  }

  /**
   * Verifies if Paypal is enabled.
   * @param _state
   */
  override isPaymentMethodEnabled(_state: StoreState): boolean {
    return !!_state.shopCheckoutState.clientToken.data?.paypalEnabled;
  }

  /**
   * Updates billing address and Paypal billing address in the store.
   * @param _address
   */
  override updateBillingAddress(_address: RmsBookstoreAddress): void {
    store.dispatch(updateBillingAddress(_address));
    store.dispatch(updatePaypalBillingAddress(_address));
  }

  /**
   * Updates shipping address in the store.
   * @param _address
   */
  override updateShippingAddress(_address: RmsBookstoreAddress): void {
    store.dispatch(updateShippingAddress(_address));
  }

  /**
   * Action to be executed when Paypal processing is complete.
   */
  override terminatePaymentMethodProcessing(): void {
    store.dispatch(updateProcessingPaypalInfo(false));
  }

  renderComp() {
    return html`
      <div class="${this.isLoading ? "d-none" : ""}">
        ${this.showPaymentButton ?
          html`
            <mhecomm-paypal-button
              amount="${this.totalAmount}"
              buttonHeightPx="40"
              currency="${this.currencyCode}"
              clientToken="${this.clientToken}"
              loaderMargins="mt-2"
              ?usePayPalShippingAddress=${true}
              ?userAutoLogin=${true}
              ?shippable=${this.shippable}>
            </mhecomm-paypal-button>
            ${this.showPaylater ? html`
              <mhecomm-paypal-button
                amount="${this.totalAmount}"
                buttonHeightPx="40"
                currency="${this.currencyCode}"
                clientToken="${this.clientToken}"
                loaderMargins="mt-2"
                ?usePayPalShippingAddress=${true}
                ?userAutoLogin=${true}
                ?paylater=${true}
                clickEventMessage="PayPal Pay Later Start"
                ?shippable=${this.shippable}>
              </mhecomm-paypal-button>
            ` : nothing}
          ` : nothing}
      </div>
      <div>
        ${this.isLoading ? html`
          ${loading({m: 'mt-2'})}
          ${this.showPaylater ? loading({m: 'mt-2'}) : nothing}
        ` : nothing}
      </div>`;
  }

  /**
   *
   * @param e collect Paypal billing address, shipping address and payment info from event details.
   * Validate and save payment info, billing and shipping address.
   */
  async onCollectPaypalInfo(e: Event) {
    store.dispatch(updateProcessingPaypalInfo(true));
    const event = e as CustomEvent;
    const paypalPaymentDetails: PaymentDetails = event.detail.paymentDetails;

    if (paypalPaymentDetails.deviceData && paypalPaymentDetails.paymentNonce && paypalPaymentDetails.paypalEmail) {
      store.dispatch(updatePaymentDetails(paypalPaymentDetails));
    }

    await this.onCollectPaymentInfo(event);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-cart-paypal-payment-method': PaypalCartPaymentMethod;
  }
}
